/**
 * API Configuration - Automatically detects the correct backend URL
 * 
 * This ensures the app works correctly in both:
 * - Preview environment (*.preview.emergentagent.com)
 * - Production environment (staff.hoppersindia.com or any custom domain)
 * 
 * The logic:
 * 1. If running on a custom domain (production), use that domain as backend
 * 2. If REACT_APP_BACKEND_URL is set and valid, use it
 * 3. Fall back to current window origin
 */

import axios from 'axios';

function getBackendUrl() {
  // Get the current hostname
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  
  // List of known production domains - add your custom domain here
  const productionDomains = [
    'staff.hoppersindia.com',
    'hoppersindia.com'
  ];
  
  // Check if we're on a production domain
  if (productionDomains.some(domain => hostname.includes(domain))) {
    return `${protocol}//${hostname}`;
  }
  
  // Check if we're on the Emergent preview domain
  if (hostname.includes('.preview.emergentagent.com')) {
    return `${protocol}//${hostname}`;
  }
  
  // Use environment variable if set (for local development)
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Fallback to current origin
  return window.location.origin;
}

// Export the backend URL
export const BACKEND_URL = getBackendUrl();

// Export the API base URL (with /api prefix)
export const API = `${BACKEND_URL}/api`;

// Helper for making authenticated requests
export function getAuthHeaders() {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
  };
}

// Create axios instance with retry logic
export const apiClient = axios.create({
  baseURL: API,
  timeout: 45000, // 45 second timeout (increased for slow connections)
});

// Track online status
let isOnline = navigator.onLine;
window.addEventListener('online', () => { isOnline = true; });
window.addEventListener('offline', () => { isOnline = false; });

// Add request interceptor for auth and online check
apiClient.interceptors.request.use((config) => {
  // Check if online before making request
  if (!isOnline) {
    return Promise.reject(new Error('No internet connection. Please check your network.'));
  }
  
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor for retry logic
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    
    // Don't retry if no config or already retrying
    if (!config || config._retry) {
      return Promise.reject(error);
    }
    
    // Determine if we should retry
    const isNetworkError = !error.response;
    const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT';
    const isServerError = error.response?.status >= 500 && error.response?.status < 600;
    const shouldRetry = isNetworkError || isTimeout || isServerError;
    
    // Retry logic (max 3 retries with exponential backoff)
    const maxRetries = 3;
    const retryCount = config._retryCount || 0;
    
    if (shouldRetry && retryCount < maxRetries) {
      config._retry = true;
      config._retryCount = retryCount + 1;
      
      // Exponential backoff: 1s, 2s, 4s
      const delay = Math.pow(2, retryCount) * 1000;
      console.log(`[API] Retry ${config._retryCount}/${maxRetries} after ${delay}ms for ${config.url}`);
      
      await new Promise(resolve => setTimeout(resolve, delay));
      
      // Reset retry flag for next attempt
      config._retry = false;
      return apiClient(config);
    }
    
    // Enhance error message for user
    if (isNetworkError) {
      error.message = 'Network error. Please check your internet connection and try again.';
    } else if (isTimeout) {
      error.message = 'Request timed out. The server might be busy, please try again.';
    }
    
    return Promise.reject(error);
  }
);

// Health check function to verify API connectivity
export async function checkApiHealth() {
  try {
    const response = await axios.get(`${API}/health`, { timeout: 10000 });
    return response.data?.status === 'healthy';
  } catch {
    return false;
  }
}

// Log the detected URL in development for debugging
if (process.env.NODE_ENV === 'development') {
  console.log('[API Config] Backend URL:', BACKEND_URL);
}
